/**
 * Minimal Widget Demo Page
 *
 * This demonstrates how to use the MinimalWidget to add voice capabilities
 * to any existing textarea or div without any built-in styling.
 */

import { useRef, useState } from 'react';
import { MinimalWidget } from '../components/MinimalWidget';

export function MinimalWidgetDemo() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');

  const handleTranscriptChange = (text: string) => {
    setTranscript(text);
  };

  const handleRecordingStateChange = (recording: boolean) => {
    setIsRecording(recording);
  };

  const startRecording = () => {
    if (textareaRef.current) {
      (textareaRef.current as any).startVoiceRecording?.();
    }
  };

  const stopRecording = () => {
    if (textareaRef.current) {
      (textareaRef.current as any).stopVoiceRecording?.();
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Minimal Widget Demo</h1>
      <p>This demo shows how to add voice capabilities to any existing element without styling.</p>

      {/* Textarea Example */}
      <div style={{ marginBottom: '30px' }}>
        <h2>Textarea Example</h2>
        <textarea
          ref={textareaRef}
          style={{
            width: '100%',
            height: '150px',
            padding: '12px',
            border: '2px solid #ccc',
            borderRadius: '8px',
            fontSize: '16px',
            fontFamily: 'monospace',
            resize: 'vertical',
          }}
          placeholder="Click 'Start Recording' and start speaking..."
        />

        <div style={{ marginTop: '10px' }}>
          <button
            type="button"
            onClick={startRecording}
            disabled={isRecording}
            style={{
              padding: '10px 20px',
              marginRight: '10px',
              backgroundColor: isRecording ? '#ccc' : '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isRecording ? 'not-allowed' : 'pointer',
            }}
          >
            {isRecording ? 'Recording...' : 'Start Recording'}
          </button>

          <button
            type="button"
            onClick={stopRecording}
            disabled={!isRecording}
            style={{
              padding: '10px 20px',
              backgroundColor: !isRecording ? '#ccc' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: !isRecording ? 'not-allowed' : 'pointer',
            }}
          >
            Stop Recording
          </button>
        </div>
      </div>

      {/* Div Example */}
      <div style={{ marginBottom: '30px' }}>
        <h2>Div Example (Content Editable)</h2>
        <div
          ref={divRef}
          contentEditable
          style={{
            width: '100%',
            minHeight: '150px',
            padding: '12px',
            border: '2px solid #ccc',
            borderRadius: '8px',
            fontSize: '16px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
          }}
          data-placeholder="Click 'Start Recording' and start speaking..."
        />
      </div>

      {/* Status Display */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Status</h3>
        <p>
          <strong>Recording:</strong> {isRecording ? 'Yes' : 'No'}
        </p>
        <p>
          <strong>Transcript:</strong>
        </p>
        <pre
          style={{
            background: '#f5f5f5',
            padding: '10px',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflow: 'auto',
          }}
        >
          {transcript || '(No transcript yet)'}
        </pre>
      </div>

      {/* Usage Instructions */}
      <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px' }}>
        <h3>Usage Instructions</h3>
        <ol>
          <li>Click "Start Recording" to begin voice input</li>
          <li>Speak clearly into your microphone</li>
          <li>Click "Stop Recording" to finish</li>
          <li>The text will appear in both the textarea and the status display</li>
          <li>You can also type/edit the text manually</li>
        </ol>

        <h4>Code Example:</h4>
        <pre
          style={{
            background: '#1e293b',
            color: '#e2e8f0',
            padding: '15px',
            borderRadius: '4px',
            fontSize: '14px',
            overflow: 'auto',
          }}
        >
          {`import { useRef } from 'react';
import { MinimalWidget } from './MinimalWidget';

function MyComponent() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  return (
    <>
      <textarea ref={textareaRef} placeholder="Speak or type..." />
      <MinimalWidget
        targetRef={textareaRef}
        onTranscriptChange={(text) => console.log(text)}
        widgetKey="your-widget-key"
      />
    </>
  );
}`}
        </pre>
      </div>

      {/* Minimal Widget Components */}
      <MinimalWidget
        targetRef={textareaRef}
        onTranscriptChange={handleTranscriptChange}
        onRecordingStateChange={handleRecordingStateChange}
        widgetKey="demo-key"
      />

      <MinimalWidget
        targetRef={divRef}
        onTranscriptChange={handleTranscriptChange}
        onRecordingStateChange={handleRecordingStateChange}
        widgetKey="demo-key"
      />
    </div>
  );
}
